import type {
  QueryObserverResult,
  RefetchOptions,
} from "@tanstack/react-query";
import { CirclePlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { forwardRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  useDeleteBranchMutation,
  useUpdateBranchMutation,
} from "@/api/admin/branches";
import { type BranchesInBrand, type IBrandGetById } from "@/api/admin/brands";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import shopOpen from "@/assets/images/shop.svg";
import { CustomButton, CustomCardBody, DetailsTitle } from "@/components/atoms";
import {
  BrandFieldEdit,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { FormProps, FormRefType } from "@/types/common";
import { commonIconProps20 } from "@/utils/constants";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";

import { type BranchesFormType } from "./types";

export interface BrandBranchFormRouter {
  toNewBranchDetails: () => void;
  toEditBranchDetails?: (branchId: string) => void;
}

export type EditBranchUrl = {
  (branchId: string): string;
  (brandId: string, branchId: string): string;
  (groupId: string, brandId: string, branchId: string): string;
};

type LocalFormProps = FormProps<BranchesFormType> & {
  router: BrandBranchFormRouter;
  editBranchUrl?: EditBranchUrl;
  refetch?: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<IBrandGetById, Error>>;
};

const BrandFormBranchesInner = forwardRef<
  FormRefType<BranchesFormType>,
  LocalFormProps
>(
  (
    {
      defaultValues,
      onSubmit,
      onValidityChange,
      id,
      router,
      editBranchUrl,
      refetch,
      classes,
    },
    ref
  ) => {
    const navigate = useNavigate();

    const { mutate: deleteBranch, isPending: isDeletePending } =
      useDeleteBranchMutation();

    const { mutate: updateBranch, isPending: isUpdatePending } =
      useUpdateBranchMutation();

    const form = useForm({
      defaultValues,
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    const handleEditBranch = (branchId: string) => {
      if (router.toEditBranchDetails) {
        router.toEditBranchDetails(branchId);
        return;
      }
      if (!editBranchUrl) return;
      navigate(editBranchUrl(branchId));
    };

    const handleDeleteBranch = (branch: BranchesInBrand) => {
      if (branch.active) {
        deleteBranch(branch.branchId, {
          onSuccess: () => {
            toastSuccess("Branch was deactivated successfully!");
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.brands, id],
            });
            refetch?.();
          },
          onError: err => {
            toastError(
              err?.message || "Failed to deactivate branch.",
              () => handleDeleteBranch(branch),
              "Error"
            );
          },
        });
      } else {
        updateBranch(
          {
            id: branch.branchId,
            data: {
              ...branch,
              active: true,
            },
          },
          {
            onSuccess: () => {
              toastSuccess("Branch was activated successfully!");
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.brands, id],
              });
              refetch?.();
            },
            onError: err => {
              toastError(
                err?.message || "Failed to activate branch.",
                () => handleDeleteBranch(branch),
                "Error"
              );
            },
          }
        );
      }
    };

    const {
      handleSubmit,
      formState: { isValid },
    } = form;

    useExposeFormValues(ref, form);

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    return (
      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id={id}
          data-testid="brand-form-vibes"
        >
          <CustomCardBody classes={cn(classes)}>
            {defaultValues?.length ? (
              <>
                <DetailsTitle title="Branches under brand" classes="mb-6" />
                <ul className="border border-gray-200 rounded-[6px] text-sm bg-white">
                  {defaultValues?.map(branch => (
                    <BrandFieldEdit
                      key={branch.branchId}
                      name={branch.branchName || ""}
                      isTrash
                      classes="py-[7px] pr-0"
                      editClik={() => {
                        handleEditBranch(branch.branchId || "");
                      }}
                      removeClick={() => {
                        handleDeleteBranch(branch as BranchesInBrand);
                      }}
                      state="branch"
                      isPending={isDeletePending || isUpdatePending}
                      isActive={branch.active}
                      disabled
                    />
                  ))}
                  <li className="rounded-md pr-4 pl-1 py-2 bg-gray-50">
                    <CustomButton
                      variant="ghost"
                      type="button"
                      onClick={() => {
                        router.toNewBranchDetails();
                      }}
                      data-testid="admin-brand-add-branch-button"
                    >
                      <CirclePlus {...commonIconProps20} />
                      Add new branch
                    </CustomButton>
                  </li>
                </ul>
              </>
            ) : (
              <div className="h-90 flex flex-col items-center  ">
                <div className="flex flex-col items-center  max-w-[230px]">
                  <img src={shopOpen} alt="image shop" />
                  <p className="font-bold mb-4 text-center">
                    It looks like you don&apos;t have any branch yet
                  </p>
                  <CustomButton
                    type="button"
                    onClick={() => {
                      router.toNewBranchDetails();
                    }}
                    data-testid="admin-account-add-button"
                    className="px-4!"
                  >
                    <CirclePlus {...commonIconProps20} />
                    Add Branch
                  </CustomButton>
                </div>
              </div>
            )}
          </CustomCardBody>
        </form>
      </Form>
    );
  }
);

BrandFormBranchesInner.displayName = "BrandFormBranches";

const BrandFormBranches = observer(BrandFormBranchesInner);
export default BrandFormBranches;
